/**
 * Current Presentation Search Tool Schema
 *
 * Dedicated semantic search within the anchored current presentation.
 */

import { z } from "zod";

export const QueryOperatorSchema = z.enum(["or", "and"])
    .describe("Combine phrases with 'or' (any phrase can match) or 'and' (all phrases should match).");

export type QueryOperator = z.infer<typeof QueryOperatorSchema>;

export const CurrentPresentationSearchParamsSchema = z.object({
    uiDescription: z.string()
        .min(1, "uiDescription is required")
        .describe("Single-line UI status text shown in the tool card."),

    phrases: z.array(z.string().min(1))
        .min(1, "At least one phrase is required")
        .describe("One or more semantic search phrases to run against the current presentation."),

    queryOperator: QueryOperatorSchema
        .optional()
        .default("or"),

    numResults: z.number()
        .int()
        .min(0)
        .max(20)
        .optional()
        .default(10)
        .describe("Maximum number of slide results to return (0-20)."),

    excludeAllPrevious: z.boolean()
        .optional()
        .default(false)
        .describe("Exclude slides that were already returned earlier in this conversation."),

    // Internal-only hook for orchestrator preflight exclusion injection.
    slidesToExclude: z.array(z.string())
        .optional()
        .describe("Optional internal list of slide IDs to exclude."),
});

export interface CurrentPresentationSearchResult {
    contextId: string;
    presentationId: string;
    slideId: string;
    resultId: string;
    slideIndex?: number;
    score: number;
    similarity?: number;
    description?: string;
    text?: string;
}

export interface CurrentPresentationSearchResponse {
    results: CurrentPresentationSearchResult[];
    count: number;
    documentId: string;
    phrases: string[];
    queryOperator: QueryOperator;
    numResults: number;
    searchTimeMs?: number;
    _imageIds?: string[];
}

export type CurrentPresentationSearchParams = z.infer<typeof CurrentPresentationSearchParamsSchema>;

